import React, { useEffect, useState, useMemo } from "react"
import { Input, Modal } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCodeMerge,
  faFolderPlus,
  faPlus,
  faSearch,
} from "@fortawesome/free-solid-svg-icons"

import PageHeader from "shared/ui/PageHeader/PageHeader.component"
import MenuButton, { MenuButtonItem } from "shared/ui/MenuButton/MenuButton.component"
import ProjectViewSwitch from "./ProjectViewSwitch.component"
import ProjectTable from "./ProjectTable/ProjectTable.component"
import AddProjectModal from "./CreateProject/AddProjectModal.component"
import { IntegrateDataModal } from "features/DataIntegration"
import NewFolderModal from "./Folders/NewFolderModal.component"
import MoveToFolderModal from "./Folders/MoveToFolderModal.component"
import RenameFolderModal from "./Folders/RenameFolderModal.component"
import {
  ProjectListResponse,
  ArchivedProjectsResponse,
  ProjectData,
} from "types/project.types"
import { ProjectView } from "features/Projects/utils/projectListState"
import {
  buildProjectTree,
  canManageMembers,
  projectRowsOf,
} from "utils/projectTree"
import { useDeleteProjectFolderMutation } from "features/Projects/api/useProjectsMutations"
import { notify } from "shared/ui/Notifications/notify"
import { useUserStore } from "stores/userStore"
import BulkSelectionBar from "features/Projects/bulkMembers/BulkSelectionBar.component"
import BulkMembersModal from "features/Projects/bulkMembers/BulkMembersModal.component"
import { BulkAction, BulkProject } from "features/Projects/bulkMembers/bulkMembers"

interface ProjectDisplayProps {
  view: ProjectView
  onViewChange: (view: ProjectView) => void
  isAdmin: boolean
  /** An admin, role resolved: may change the members of anyone's project. */
  canManageAll: boolean
  isLoading: boolean
  isLoadingArchived: boolean
  projects: ProjectListResponse
  archivedProjects: ArchivedProjectsResponse
  folders: string[]
  /** Folder paths shown open, and a folder to bring into view (see ProjectTable). */
  openFolders: string[]
  onOpenFoldersChange: (folders: string[]) => void
  revealFolder?: string | null
  onRevealDone?: () => void
  handleUnarchive: (projectId: string, owner: string) => Promise<void> | void
  handleArchive: (projectId: string, owner: string) => Promise<void> | void
}

const ProjectDisplay: React.FC<ProjectDisplayProps> = ({
  view,
  onViewChange,
  isAdmin,
  canManageAll,
  isLoading,
  isLoadingArchived,
  projects,
  archivedProjects,
  folders,
  openFolders,
  onOpenFoldersChange,
  revealFolder,
  onRevealDone,
  handleUnarchive,
  handleArchive,
}) => {
  const showArchived = view === "archived"
  const [search, setSearch] = useState<string>("")

  const deleteFolderMutation = useDeleteProjectFolderMutation()

  const [newProjectOpen, setNewProjectOpen] = useState<boolean>(false)
  const [newFolderOpen, setNewFolderOpen] = useState<boolean>(false)
  const [integrateOpen, setIntegrateOpen] = useState<boolean>(false)
  const [moveProject, setMoveProject] = useState<ProjectData | null>(null)
  const [renameFolder, setRenameFolder] = useState<string | null>(null)

  const username = useUserStore((state) => state.user?.username ?? "")
  // Ticked project ids, for adding or removing members on all of them.
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  // The members dialog to open on the ticked projects, if any.
  const [bulkAction, setBulkAction] = useState<BulkAction | null>(null)

  // One list, so branches with more ways to start something add an entry.
  const newItems: MenuButtonItem[] = [
    {
      key: "project",
      label: "Project",
      icon: <FontAwesomeIcon icon={faPlus} />,
      onClick: () => setNewProjectOpen(true),
    },
    {
      key: "folder",
      label: "Folder",
      icon: <FontAwesomeIcon icon={faFolderPlus} />,
      onClick: () => setNewFolderOpen(true),
    },
    {
      key: "integrate",
      label: "Integrate data",
      icon: <FontAwesomeIcon icon={faCodeMerge} />,
      onClick: () => setIntegrateOpen(true),
    },
  ]

  const treeRows = useMemo(
    () =>
      buildProjectTree(
        showArchived
          ? archivedProjects?.result || []
          : projects?.result || [],
        folders
      ),
    [showArchived, archivedProjects, projects, folders]
  )

  // Another view or search starts with nothing ticked, so a change only ever
  // reaches projects the user can see.
  useEffect(() => {
    setSelectedIds((ids) => (ids.length === 0 ? ids : []))
  }, [view, search])

  // The ticked projects still listed that the user may change, as the members
  // run takes them. A project gone from the list (archived, say) drops out.
  const selectedProjects: BulkProject[] = useMemo(() => {
    if (showArchived || selectedIds.length === 0) return []
    const ticked = new Set(selectedIds)
    return projectRowsOf(treeRows)
      .filter(
        (row) =>
          ticked.has(row.id) && canManageMembers(row, username, canManageAll)
      )
      .map((row) => ({
        id: row.id,
        name: row.metadata?.Name ?? row.name,
        owner: row.owner,
      }))
  }, [showArchived, selectedIds, treeRows, username, canManageAll])

  const selectedProjectIds = useMemo(
    () => selectedProjects.map((project) => project.id),
    [selectedProjects]
  )

  const handleDeleteFolder = (folderPath: string, count: number): void => {
    const confirmDelete = async () => {
      try {
        await deleteFolderMutation.mutateAsync(folderPath)
        notify.success(
          "Folder deleted",
          count > 0
            ? `Deleted '${folderPath}'. ${count} project(s) moved to ungrouped.`
            : `Deleted '${folderPath}'.`
        )
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unknown error"
        notify.error("Error deleting folder", message)
      }
    }

    if (count === 0) {
      confirmDelete()
      return
    }

    Modal.confirm({
      title: `Delete folder '${folderPath}'?`,
      content: `This folder contains ${count} project(s). They will become ungrouped.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: confirmDelete,
    })
  }

  return (
    <div className="flex flex-col p-5">
      <div className="mb-4">
        <PageHeader
          title="Projects"
          left={
            <ProjectViewSwitch
              value={view}
              onChange={onViewChange}
              isAdmin={isAdmin}
            />
          }
          right={
            selectedProjects.length > 0 ? (
              <BulkSelectionBar
                count={selectedProjects.length}
                onAdd={() => setBulkAction("add")}
                onRemove={() => setBulkAction("remove")}
                onClear={() => setSelectedIds([])}
              />
            ) : (
              <>
                <Input
                  allowClear
                  prefix={<FontAwesomeIcon icon={faSearch} />}
                  placeholder="Search projects"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: 200 }}
                />
                <MenuButton primary label="New" items={newItems} />
              </>
            )
          }
        />
      </div>

      <ProjectTable
        rows={treeRows}
        searchTerm={search}
        isLoading={showArchived ? isLoadingArchived : isLoading}
        isArchived={showArchived}
        handleUnarchive={handleUnarchive}
        handleArchive={handleArchive}
        onRenameFolder={(folderPath) => setRenameFolder(folderPath)}
        onDeleteFolder={handleDeleteFolder}
        onMoveProject={(project) => setMoveProject(project)}
        openFolders={openFolders}
        onOpenFoldersChange={onOpenFoldersChange}
        revealFolder={revealFolder}
        onRevealDone={onRevealDone}
        selectedIds={selectedProjectIds}
        onSelectionChange={setSelectedIds}
        canManageAll={canManageAll}
      />

      <AddProjectModal
        open={newProjectOpen}
        onClose={() => setNewProjectOpen(false)}
      />
      <IntegrateDataModal
        open={integrateOpen}
        onClose={() => setIntegrateOpen(false)}
      />
      <NewFolderModal
        open={newFolderOpen}
        onClose={() => setNewFolderOpen(false)}
        existingFolders={folders}
      />
      <MoveToFolderModal
        open={moveProject !== null}
        onClose={() => setMoveProject(null)}
        project={moveProject}
        existingFolders={folders}
      />
      <RenameFolderModal
        open={renameFolder !== null}
        onClose={() => setRenameFolder(null)}
        folderPath={renameFolder}
      />
      {bulkAction !== null && (
        <BulkMembersModal
          open
          action={bulkAction}
          projects={selectedProjects}
          onClose={(finished, keepIds) => {
            setBulkAction(null)
            // After a run only the projects that didn't finish stay ticked,
            // for another go; a cancel keeps every tick.
            if (finished) setSelectedIds(keepIds)
          }}
        />
      )}
    </div>
  )
}

export default ProjectDisplay
